import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
	getSingleComponentCss,
	ComponentCss,
	getChildrenAndSlots,
	stringifySingleComponentCss,
	accumulateCssContext,
} from '@wix/thunderbolt-catharsis-extensions'
import { Component } from '@wix/thunderbolt-becky-types'
import { refApi } from '@wix/thunderbolt-refs'
import _ from 'lodash'
import {
	ComponentCssContextValue,
	StructureComponentCssProps,
	ComponentsCssProps,
	MegaStoreContextValue,
} from './ComponentsCss.types'
import { debounce } from './debounce'
import { Ref } from '@wix/materializer'

const ComponentCssContext = React.createContext<ComponentCssContextValue>({} as ComponentCssContextValue)

const StructureComponentCss = React.memo(
	({ id, ancestorComponentBreakpointsOrder, cssContext = {} }: StructureComponentCssProps) => {
		const { getByCompId, subscribeByCompId, experiments } = useContext<ComponentCssContextValue>(
			ComponentCssContext
		)

		const data = getByCompId(id)
		const { structure, breakpointsOrder = ancestorComponentBreakpointsOrder, compSpecificData, ...compCss } = data

		const [, setTick] = useState(0)
		const forceUpdate = useCallback(() => setTick((tick) => tick + 1), [])

		const getParent = useCallback(() => ({ getParent: cssContext?.getParent, componentId: id }), [
			cssContext?.getParent,
			id,
		])

		const accumulatedCssContext = accumulateCssContext(id, { ...cssContext, getParent }, compSpecificData)

		// eslint-disable-next-line react-hooks/exhaustive-deps
		useEffect(() => subscribeByCompId(id, forceUpdate), [])

		if (!structure) {
			return null
		}

		const singleComponentCss = getSingleComponentCss(
			structure,
			breakpointsOrder,
			accumulatedCssContext,
			compCss,
			experiments
		)
		const cssString = stringifySingleComponentCss(singleComponentCss)
		const currentComp = <style data-comp-id={id}>{cssString}</style>

		accumulatedCssContext.isInRepeater =
			accumulatedCssContext.isInRepeater || structure.componentType === 'Repeater'
		const children = getChildrenAndSlots(structure).map((childId) => (
			<StructureComponentCss
				key={childId}
				id={childId}
				cssContext={accumulatedCssContext}
				ancestorComponentBreakpointsOrder={breakpointsOrder}
			/>
		))
		return (
			<React.Fragment>
				{currentComp}
				{children}
			</React.Fragment>
		)
	},
	(prevProps, nextProps) => _.isEqual(prevProps, nextProps)
)

export const ComponentsCss: React.FC<ComponentsCssProps> = (props) => {
	const { megaStore, batchingStrategy } = useContext(MegaStoreContext)
	const structureStore = megaStore.getChildStore('structure')
	const cssStore = megaStore.getChildStore('componentCss')

	const getByCompId = (compId: string) => ({
		structure: structureStore.getById<Component>(compId),
		...cssStore.getById<ComponentCss>(compId),
	})

	const stores = [structureStore, cssStore]

	const subscribeByCompId = (compId: string, callback: () => void) => {
		const debounced = debounce(() => batchingStrategy.batch(callback))
		const unsubscribes = stores.map((store) => store.subscribeById(compId, debounced))
		return () => unsubscribes.forEach((unsubscribe) => unsubscribe())
	}

	const storesContextValue = { getByCompId, subscribeByCompId, experiments: props.experiments }

	const pageId = useExportedValue(refApi.router.pageId())
	const lightboxId = useExportedValue(refApi.lightbox.lightboxPageId())

	return (
		<ComponentCssContext.Provider value={storesContextValue}>
			<StructureComponentCss id="masterPage" key="masterPage" />
			{pageId && <StructureComponentCss id={pageId} key={pageId} />}
			{lightboxId && <StructureComponentCss id={lightboxId} key={lightboxId} />}
		</ComponentCssContext.Provider>
	)
}

const MegaStoreContext = React.createContext<MegaStoreContextValue>({} as MegaStoreContextValue)

export const MegaStoreProvider: React.FC<MegaStoreContextValue> = ({ children, ...props }) => {
	return <MegaStoreContext.Provider value={props}>{children}</MegaStoreContext.Provider>
}

function useExportedValue<T>(ref: Ref<T>): T {
	const { megaStore, batchingStrategy } = useContext(MegaStoreContext)
	const [subStore1, subStore2, id, ...innerPath] = ref.refPath
	const subStore = megaStore.getChildStore(subStore1).getChildStore(subStore2)
	const [value, setValue] = useState(subStore.getById<any>(id))
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => subStore.subscribeById(id, (__, newValue) => batchingStrategy.batch(() => setValue(newValue))), [
		ref,
	])
	return innerPath.reduce((acc, path) => acc[path], value) as T
}
